import { ArrowBack, DeleteOutline, HighlightAlt } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Divider, IconButton, Tooltip, TextField } from "@mui/material";
import { displayDateAndTimeCompact } from "Backend";
import PCheckbox from "components/PCheckbox";
import { AnnotationsContext } from "contexts/AnnotationsContext";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { useFormik } from "formik";
import {
  Block,
  CreateDocumentAnnotationDocument,
  DeleteDocumentAnnotationDocument,
  EditDocumentAnnotationDocument,
} from "generated/graphql";
import { runGqlMutation } from "gqlClient";
import { useSnackbar } from "notistack";
import { useContext, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectMyUserId } from "redux/slices/session";
import { P } from "theme/typography";

interface AnnotationEditorProps {
  documentId: number;
  pageNumber: number;
  blocks: Block[];
}

const alertStyles = {
  alignItems: "center",
  "& .MuiAlert-message": {
    p: 0,
    fontSize: "0.75rem",
  },
  "& .MuiAlert-icon": {
    p: 0,
  },
};

const AnnotationEditor = (props: AnnotationEditorProps) => {
  const {
    documentAnnotations,
    setDocumentAnnotations,
    selectedDocumentAnnotation,
    setSelectedDocumentAnnotation,
    handleClose,
    drawing,
    findCreatorName,
    findEditorName,
    loadBlocksAndAnnotations,
    popoverOpen,
    setPopoverOpen,
  } = useContext(AnnotationsContext);
  const [isOperationPending, setIsOperationPending] = useState(false);
  const userId = useAppSelector(selectMyUserId);
  const { enqueueSnackbar } = useSnackbar();
  const formik = useFormik({
    initialValues: {
      body: selectedDocumentAnnotation?.note || "",
      exportInclude: selectedDocumentAnnotation?.exportInclude ?? true,
    },
    onSubmit: () => void 0,
  });

  const confirmCtx = useContext(ConfirmationDialogContext);

  const createAnnotation = () => {
    if (!drawing.boundingBox) {
      return;
    }
    setIsOperationPending(true);
    runGqlMutation(CreateDocumentAnnotationDocument, {
      documentId: props.documentId,
      pageNumber: props.pageNumber,
      note: formik.values.body,
      boundingBox: drawing.boundingBox,
      exportInclude: formik.values.exportInclude,
    })
      .then(() => {
        setIsOperationPending(false);
        loadBlocksAndAnnotations();
        handleClose();
      })
      .catch(() => {
        enqueueSnackbar("Failed to create annotation", { variant: "error" });
        setIsOperationPending(false);
      });
  };

  const updateAnnotation = () => {
    if (!selectedDocumentAnnotation) {
      return;
    }
    setIsOperationPending(true);
    runGqlMutation(EditDocumentAnnotationDocument, {
      id: selectedDocumentAnnotation.id,
      note: formik.values.body,
      boundingBox: drawing.boundingBox || selectedDocumentAnnotation.boundingBox,
      exportInclude: formik.values.exportInclude,
    })
      .then(() => {
        setIsOperationPending(false);
        setDocumentAnnotations(
          documentAnnotations.map((a) => {
            if (a.id === selectedDocumentAnnotation?.id) {
              return {
                ...a,
                note: formik.values.body,
                boundingBox: drawing.boundingBox || a.boundingBox,
                exportInclude: formik.values.exportInclude,
                editedBy: userId,
                editedAt: new Date().toISOString(),
              };
            }
            return a;
          })
        );
        handleClose();
      })
      .catch(() => {
        enqueueSnackbar("Failed to update annotation", { variant: "error" });
        setIsOperationPending(false);
      });
  };

  const handleDeleteAnnotation = () => {
    confirmCtx.confirm({
      title: "Delete annotation",
      prompt: <P variant="sm">Are you sure you want to delete this annotation?</P>,
      callback: () => {
        setIsOperationPending(true);
        if (!selectedDocumentAnnotation || !selectedDocumentAnnotation.id) {
          return Promise.resolve();
        }
        return runGqlMutation(DeleteDocumentAnnotationDocument, {
          id: selectedDocumentAnnotation?.id,
        })
          .then(() => {
            setIsOperationPending(false);
            setDocumentAnnotations((prev) => prev.filter((a) => a.id !== selectedDocumentAnnotation?.id));
            handleClose();
          })
          .catch(() => {
            setIsOperationPending(false);
            enqueueSnackbar("Failed to delete annotation", { variant: "error" });
          });
      },
    });
  };

  return (
    <Box
      sx={{
        width: "280px",
        display: "flex",
      }}>
      <Divider orientation="vertical" />
      <Box sx={{ width: "280px", backgroundColor: "white" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            gap: 1,
            alignItems: "center",
            backgroundColor: "white",
            py: 1,
            pl: 1.5,
          }}>
          {!popoverOpen && (
            <Tooltip title="Go back" arrow>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{
                  m: 0,
                  mr: 0.5,
                }}>
                <ArrowBack />
              </IconButton>
            </Tooltip>
          )}
          <P variant="lgBold">{selectedDocumentAnnotation ? "Edit" : "New"} Annotation</P>
        </Box>
        <Divider />
        <Box
          sx={{
            px: 1,
          }}>
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", my: 1, gap: 1 }}>
              {drawing.boundingBox && selectedDocumentAnnotation && (
                <Alert
                  severity="info"
                  sx={alertStyles}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        drawing.setEnabled(false);
                        drawing.setBoundingBox(undefined);
                      }}>
                      UNDO
                    </Button>
                  }>
                  Selection modified
                </Alert>
              )}
              <Box>
                <TextField
                  multiline
                  rows={!popoverOpen ? Math.min(Math.max(formik.values.body.split("\n").length, 4), 10) : 4}
                  fullWidth={true}
                  placeholder="Note"
                  error={formik.touched.body && Boolean(formik.errors.body)}
                  helperText={formik.touched.body && formik.errors.body}
                  onBlur={formik.handleBlur}
                  value={formik.values.body}
                  onChange={formik.handleChange}
                  id="body"
                  name="body"
                />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                <PCheckbox
                  label="Include in Exports"
                  checked={formik.values.exportInclude}
                  onChange={formik.handleChange}
                  name="exportInclude"
                  id="exportInclude"
                />
                <Box
                  sx={{
                    display: "flex",
                  }}>
                  {selectedDocumentAnnotation && (
                    <Tooltip title={drawing.enabled ? "Redrawing" : "Redraw"} arrow>
                      <IconButton
                        onClick={() => {
                          if (!drawing.enabled) {
                            // user is about draw, lets get the popover out of the way
                            setPopoverOpen(false);
                          }
                          // always toggle the drawing state and reset the old drawing
                          drawing.setEnabled((prev) => !prev);
                          drawing.setBoundingBox(undefined);
                        }}>
                        <HighlightAlt
                          sx={{
                            color: drawing.enabled ? "primary.main" : "black",
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  {selectedDocumentAnnotation && (
                    <Tooltip title="Delete" arrow>
                      <IconButton onClick={handleDeleteAnnotation}>
                        <DeleteOutline />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </Box>
          </form>

          {selectedDocumentAnnotation && (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
              <P variant="xs">
                Created by {findCreatorName(selectedDocumentAnnotation) || ""} •{" "}
                {displayDateAndTimeCompact(selectedDocumentAnnotation.createdAt)}
              </P>

              {selectedDocumentAnnotation.editedAt && (
                <P variant="xs">
                  Updated by {findEditorName(selectedDocumentAnnotation) || ""} •{" "}
                  {displayDateAndTimeCompact(selectedDocumentAnnotation.editedAt)}
                </P>
              )}
            </Box>
          )}

          {/* Redraw Button */}
          <Box
            sx={{
              gap: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              mt: 2,
            }}>
            <Button
              size="small"
              variant="text"
              onClick={() => {
                setSelectedDocumentAnnotation(undefined);
                handleClose();
              }}
              sx={{
                display: formik.dirty || drawing.boundingBox ? "block" : "none",
              }}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              disabled={!selectedDocumentAnnotation && !drawing.boundingBox}
              onClick={() => {
                selectedDocumentAnnotation ? updateAnnotation() : createAnnotation();
              }}
              loading={isOperationPending}
              size="small">
              Save
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AnnotationEditor;
